import { normalizeIconUrl } from "@/lib/iconUrl";
import { type PreorderStatus } from "@/lib/products";

/**
 * Helper murni beranda Shopee-style (HomeClient): filter feed, rail "PO Segera
 * Ditutup"/"Produk Terbaru", hitung sisa hari deadline, dan visual tile
 * kategori/merek. Tanpa React/Supabase supaya bisa diuji langsung di Vitest.
 */

export type HomeProductType = "preorder" | "ready_stock";

/**
 * Subset RINGAN produk yang dibutuhkan SEMUA logika discovery beranda
 * (filter/rail/tile/hitung tab). Sengaja TANPA gambar & varian — itu data berat
 * yang hanya dihidrasi untuk kartu yang benar-benar terlihat (lihat
 * `getPublicProductsByIds`). `Product` memenuhi tipe ini secara struktural, jadi
 * helper di bawah generik dan tetap bisa diuji dengan objek `Product` penuh.
 */
export type HomeCatalogItem = {
  id: string;
  slug: string;
  /**
   * OPSIONAL sejak PF-01C (audit 2026-08): index katalog tidak lagi membawa
   * `name` — beranda tak pernah membacanya (feed selalu `query: ""`; pencarian
   * hidup di `/cari` via RPC). `Product` penuh tetap memenuhi tipe ini.
   */
  name?: string;
  brand: string;
  category?: string;
  /** Segmen produk (Anak/Dewasa, mig 159) — sumbu filter terpisah dari kategori. */
  segment?: string;
  productType?: "preorder" | "ready_stock";
  status: PreorderStatus;
  deadline: string;
  /**
   * `products.updated_at` — kapan produk ini TERAKHIR BENAR-BENAR berubah.
   *
   * Ada di sini semata untuk `lastmod` sitemap, dan itu sebabnya ia OPSIONAL:
   * `Product` penuh (yang juga memenuhi tipe ini) tak selalu membawanya, dan
   * tak satu pun tampilan storefront membacanya. Pemakainya `src/app/sitemap.ts`
   * — yang TIDAK boleh mengarang tanggal saat nilainya tak ada.
   */
  updatedAt?: string;
};

/** Jumlah kartu per batch feed (dipakai HomeClient & hidrasi awal server). */
export const HOME_FEED_BATCH = 12;

export type HomeFilters = {
  type: HomeProductType;
  category: string | null;
  brand: string | null;
  /** Segmen aktif (drill-down kategori, mig 159). Opsional → null. */
  segment?: string | null;
  query: string;
};

const normalize = (value: string | undefined | null) =>
  (value ?? "").trim().toLowerCase();

/**
 * Tipe efektif produk untuk tab beranda. `productType` kosong dianggap
 * 'preorder' (mengikuti default `mapPublicProduct`). Diekspor untuk feed geser
 * halaman produk (`productFeed.ts`) — satu aturan, bukan salinan.
 */
export function effectiveType(
  product: Pick<HomeCatalogItem, "productType">
): HomeProductType {
  return product.productType === "ready_stock" ? "ready_stock" : "preorder";
}

/**
 * Apakah produk layak muncul di permukaan discovery storefront (feed, search,
 * rail, tile kategori/merek, hitung tab)? Ready stock selalu tampil. Preorder
 * HANYA saat `OPEN PO` — preorder yang sudah CLOSED/PRODUKSI/READY barangnya
 * sudah dialokasikan ke pemesan PO lama, jadi disembunyikan dari reseller baru
 * (halaman produknya tetap bisa dibuka via link/Pesanan, hanya tanpa tombol
 * pesan — lihat `isOrderableStatus`). Mencegah reseller baru menemukan +
 * "menumpang" pesan ke PO yang sudah ditutup.
 */
export function isDiscoverableProduct(product: HomeCatalogItem): boolean {
  if (product.productType === "ready_stock") return true;
  return product.status === "OPEN PO";
}

export function discoverableProducts<T extends HomeCatalogItem>(
  products: T[]
): T[] {
  return products.filter(isDiscoverableProduct);
}

export type HomeTypeCounts = { preorder: number; ready_stock: number };

/**
 * Hitung produk per tab tipe beranda (badge "N produk" di TypeTab + label feed
 * "Rekomendasi"). PF-01B: dihitung DI SERVER dari index penuh lalu dikirim
 * sebagai prop supaya klien tak butuh index penuh untuk angka yang benar; klien
 * menghitung ulang dengan fungsi YANG SAMA setelah index deferred termuat.
 * `productType` kosong dianggap 'preorder' (cermin `effectiveType`).
 */
export function countProductsByType(products: HomeCatalogItem[]): HomeTypeCounts {
  let preorder = 0;
  let ready = 0;
  for (const product of products) {
    if (product.productType === "ready_stock") ready += 1;
    else preorder += 1;
  }
  return { preorder, ready_stock: ready };
}

/**
 * Nama master (tabel `product_categories`/`product_brands`) yang benar-benar
 * punya produk — untuk tile Kategori & chip Merek beranda. Master adalah satu-
 * satunya sumber kebenaran: nama yang sudah dihapus admin TIDAK di-derive dari
 * snapshot teks `products.category`/`brand`, jadi tile/chip ikut hilang setelah
 * dihapus; master kosong → tidak ada tile/chip. Dulu duplikat privat di
 * HomeClient & ResellerCatalogClient (`visibleNames`); diekstrak saat PF-01B
 * karena server kini menghitungnya juga (dari index penuh) untuk paint awal.
 */
export function visibleMasterNames(
  master: Array<{ name: string }>,
  productValues: Array<string | undefined>
): string[] {
  const present = new Set(
    productValues
      .map((value) => (value ?? "").trim().toLowerCase())
      .filter(Boolean)
  );
  return master
    .map((item) => item.name.trim())
    .filter(Boolean)
    .filter((name) => present.has(name.toLowerCase()));
}

/**
 * Filter feed beranda. `productType` kosong dianggap 'preorder' (mengikuti
 * default `mapPublicProduct`). Saat `query` terisi, pencarian melintasi KEDUA
 * tipe (tab tipe diabaikan) — meniru surface pencarian Shopee.
 */
export function filterHomeProducts<T extends HomeCatalogItem>(
  products: T[],
  filters: HomeFilters
): T[] {
  const query = normalize(filters.query);
  const category = filters.category ? normalize(filters.category) : null;
  const brand = filters.brand ? normalize(filters.brand) : null;
  const segment = filters.segment ? normalize(filters.segment) : null;

  return products.filter((product) => {
    const matchesType = query ? true : effectiveType(product) === filters.type;
    const matchesCategory = !category || normalize(product.category) === category;
    const matchesBrand = !brand || normalize(product.brand) === brand;
    const matchesSegment = !segment || normalize(product.segment) === segment;
    const matchesQuery =
      !query ||
      (product.name ?? "").toLowerCase().includes(query) ||
      product.brand.toLowerCase().includes(query) ||
      (product.category ?? "").toLowerCase().includes(query);

    return matchesType && matchesCategory && matchesBrand && matchesSegment && matchesQuery;
  });
}

export type CategoryCount = { name: string; count: number };

/**
 * Daftar kategori master yang punya produk discoverable + jumlah produknya —
 * untuk LANDING halaman Kategori (`/kategori`, grid semua kategori). Master =
 * sumber kebenaran (kategori yang sudah dihapus admin tidak muncul walau
 * snapshot teks `products.category` masih ada). Hanya kategori ber-count > 0;
 * urut sesuai master. Count lintas tipe (Pre-Order + Ready Stock).
 */
export function categoryProductCounts<T extends HomeCatalogItem>(
  products: T[],
  categoryMaster: string[]
): CategoryCount[] {
  const counts = new Map<string, number>();
  for (const product of products) {
    const key = normalize(product.category);
    if (!key) continue;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return categoryMaster
    .map((name) => name.trim())
    .filter(Boolean)
    .map((name) => ({ name, count: counts.get(name.toLowerCase()) ?? 0 }))
    .filter((entry) => entry.count > 0);
}

/**
 * Daftar merek master yang punya produk discoverable + jumlahnya — untuk LANDING
 * halaman Merek (`/merek`, daftar semua merek). Cermin `categoryProductCounts`:
 * master = sumber kebenaran (merek terhapus tak muncul), hanya ber-count > 0,
 * urut sesuai master.
 */
export function brandProductCounts<T extends HomeCatalogItem>(
  products: T[],
  brandMaster: string[]
): CategoryCount[] {
  const counts = new Map<string, number>();
  for (const product of products) {
    const key = normalize(product.brand);
    if (!key) continue;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return brandMaster
    .map((name) => name.trim())
    .filter(Boolean)
    .map((name) => ({ name, count: counts.get(name.toLowerCase()) ?? 0 }))
    .filter((entry) => entry.count > 0);
}

/**
 * Segmen master yang punya ≥1 produk discoverable — untuk chip shortcut segmen
 * di LANDING halaman Kategori (telusur segmen lintas-kategori). Master = sumber
 * kebenaran; urut sesuai master. Mengabaikan segmen kosong.
 */
export function segmentsWithProducts<T extends HomeCatalogItem>(
  products: T[],
  segmentMaster: string[]
): string[] {
  const present = new Set(
    products.map((product) => normalize(product.segment)).filter(Boolean)
  );
  return segmentMaster
    .map((name) => name.trim())
    .filter(Boolean)
    .filter((name) => present.has(name.toLowerCase()));
}

/**
 * Sisa hari menuju deadline PO. `deadline` dipercaya hanya bila berawalan ISO
 * `YYYY-MM-DD` (kolom `products.preorder_deadline` bertipe date); selain itu
 * (kosong/teks lama) → null. Tanggal dibangun dari komponen numerik agar tetap
 * tengah-malam LOKAL — `new Date("YYYY-MM-DD")` di-parse sebagai UTC dan bisa
 * bergeser sehari di zona +7.
 */
export function daysUntilDeadline(deadline: string, now: Date): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(deadline.trim());
  if (!match) return null;

  const target = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  );
  if (Number.isNaN(target.getTime())) return null;

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

export function deadlineChipLabel(days: number): string {
  if (days <= 0) return "Hari terakhir!";
  return `${days} hari lagi`;
}

/**
 * Tanggal tutup PO utk baris "Tutup PO : DD-MM-YYYY" di kartu produk. Sama
 * seperti `daysUntilDeadline`, hanya percaya deadline berawalan ISO
 * `YYYY-MM-DD`; teks lama/kosong → null (baris tidak dirender).
 */
export function deadlineDateLabel(deadline: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(deadline.trim());
  if (!match) return null;
  return `${match[3]}-${match[2]}-${match[1]}`;
}

/**
 * Produk OPEN PO (non-ready stock) yang deadline-nya belum lewat, terdekat dulu.
 *
 * `limit` null = tanpa batas (halaman /po-segera-ditutup); angka = potong
 * sekian teratas (rail beranda, default 10). Konvensi `null` disamakan dengan
 * `railProducts` supaya satu idiom saja yang perlu diingat.
 */
export function closingSoonProducts<T extends HomeCatalogItem>(
  products: T[],
  now: Date,
  limit: number | null = 10
): T[] {
  const sorted = products
    .map((product) => ({
      product,
      days:
        product.productType !== "ready_stock" && product.status === "OPEN PO"
          ? daysUntilDeadline(product.deadline, now)
          : null
    }))
    .filter((entry): entry is { product: T; days: number } =>
      entry.days !== null && entry.days >= 0
    )
    .sort((a, b) => a.days - b.days)
    .map((entry) => entry.product);

  return limit === null ? sorted : sorted.slice(0, Math.max(0, limit));
}

/**
 * Saring produk ke satu tipe tab beranda (preorder/ready_stock). `productType`
 * kosong dianggap 'preorder'. Dipakai rail per-tab "Terakhir Dilihat" & "Produk
 * Terbaru" supaya tiap tab hanya menampilkan produk tipenya.
 */
export function productsByType<T extends HomeCatalogItem>(
  products: T[],
  type: HomeProductType
): T[] {
  return products.filter((product) => effectiveType(product) === type);
}

/**
 * Produk terbaru (index sudah terurut created_at desc → ambil N teratas). Bila
 * `type` diberikan, batasi ke tipe itu saja supaya rail "Produk Terbaru"
 * terpisah per tab: produk PO baru hanya muncul di tab Pre-Order, ready stock
 * baru hanya di tab Ready Stock. `type` null = lintas tipe.
 */
export function newestProducts<T extends HomeCatalogItem>(
  products: T[],
  type: HomeProductType | null = null,
  limit = 10
): T[] {
  const source = type === null ? products : productsByType(products, type);
  return source.slice(0, limit);
}

export type CategoryIconKey =
  | "bag"
  | "baby"
  | "dress"
  | "shirt"
  | "shoe"
  | "hijab"
  | "accessory"
  | "watch"
  | "outer"
  | "generic";

const CATEGORY_ICON_RULES: Array<{ key: CategoryIconKey; words: string[] }> = [
  { key: "bag", words: ["tas", "bag", "ransel", "sling", "tote", "dompet", "pouch"] },
  { key: "baby", words: ["anak", "kids", "baby", "bayi", "balita"] },
  { key: "hijab", words: ["hijab", "jilbab", "kerudung", "pashmina", "khimar", "bergo"] },
  { key: "dress", words: ["dress", "gamis", "rok", "tunik", "kebaya"] },
  { key: "outer", words: ["outer", "jaket", "cardigan", "sweater", "hoodie", "blazer", "coat"] },
  { key: "shirt", words: ["blouse", "baju", "kaos", "kemeja", "atasan", "setelan", "celana", "shirt"] },
  { key: "shoe", words: ["sepatu", "sandal", "heels", "sneaker", "flat shoes"] },
  { key: "watch", words: ["jam"] },
  { key: "accessory", words: ["aksesoris", "aksesori", "gelang", "kalung", "anting", "cincin", "bros"] }
];

/** Petakan nama kategori (teks bebas dari admin) ke kunci ikon lucide di CategoryRow. */
export function categoryIconKey(name: string): CategoryIconKey {
  const normalized = normalize(name);
  if (!normalized) return "generic";

  for (const rule of CATEGORY_ICON_RULES) {
    if (rule.words.some((word) => normalized.includes(word))) {
      return rule.key;
    }
  }
  return "generic";
}

// String kelas LITERAL utuh — Tailwind JIT hanya men-scan literal di source.
const TILE_CLASSES = [
  "bg-orange-100 text-orange-600",
  "bg-sky-100 text-sky-600",
  "bg-emerald-100 text-emerald-600",
  "bg-violet-100 text-violet-600",
  "bg-amber-100 text-amber-700",
  "bg-pink-100 text-pink-600"
];

/** Warna pastel deterministik per nama (hash jumlah char code) — stabil antar render. */
export function categoryTileClass(name: string): string {
  const normalized = normalize(name);
  let hash = 0;
  for (let i = 0; i < normalized.length; i += 1) {
    hash += normalized.charCodeAt(i);
  }
  return TILE_CLASSES[hash % TILE_CLASSES.length];
}

/** Inisial merek untuk avatar chip (karakter non-spasi pertama, uppercase). */
export function brandInitial(name: string): string {
  const first = name.trim().charAt(0);
  return first ? first.toUpperCase() : "?";
}

/**
 * Peta nama master → URL logo unggahan, dipakai DUA sumbu katalog: merek
 * (`product_brands.icon_url`, mig 096 — BrandRow, landing `/merek`) dan
 * kategori (`product_categories.icon_url`, mig 0951 — CategoryRow, landing
 * `/kategori`). Satu helper karena masalahnya identik; yang membedakan cuma
 * tabel asalnya.
 *
 * Key DINORMALISASI (trim + lowercase) karena `products.brand`/`products.category`
 * cuma SNAPSHOT TEKS, bukan FK — kapitalisasi/spasi bisa menyimpang dari master,
 * jadi pencocokan huruf-per-huruf akan menjatuhkan logo yang sebenarnya ada.
 *
 * URL digerbangi `normalizeIconUrl`: nilai yang bukan URL http(s) / path lokal
 * TIDAK masuk peta, jadi pemanggil jatuh ke ikon bawaannya (avatar inisial untuk
 * merek, ikon lucide untuk kategori) alih-alih merender gambar RUSAK. Master
 * tanpa logo pun tidak masuk — cabang "kalau iconUrl kosong…" cukup ada di sini,
 * satu kali, bukan di tiap pemanggil.
 */
export function masterLogoMap(
  items: Array<{ name: string; iconUrl?: string | null }>
): Map<string, string> {
  const map = new Map<string, string>();
  for (const item of items) {
    const key = normalize(item.name);
    const url = normalizeIconUrl(item.iconUrl);
    if (!key || !url) continue;
    // Master duplikat (beda kapitalisasi) → entri PERTAMA menang, mengikuti
    // urutan master dari server (nama menaik) supaya hasilnya deterministik.
    if (!map.has(key)) map.set(key, url);
  }
  return map;
}

/** Logo dari peta `masterLogoMap` — pemanggil kirim nama apa adanya. */
export function masterLogoOf(
  logos: ReadonlyMap<string, string> | undefined,
  name: string
): string | undefined {
  return logos?.get(normalize(name));
}

/** Jumlah foto produk yang ditumpuk di tile kategori. */
export const CATEGORY_COVER_PHOTOS = 2;

/**
 * Kandidat produk per kategori yang foto sampulnya ditarik. Sengaja LEBIH dari
 * `CATEGORY_COVER_PHOTOS`: produk terbaru yang belum berfoto (atau berfoto sama
 * persis dengan produk lain — duplikat produk ikut menyalin medianya) dilompati
 * tanpa kategorinya kehilangan tumpukan. Tetap kecil karena tiap kandidat
 * berarti seluruh baris `product_media`-nya ikut ditarik.
 */
export const CATEGORY_COVER_CANDIDATES = 4;

/**
 * Foto tile kategori per kategori, key DINORMALISASI (alasan sama dengan
 * `masterLogoMap`: `products.category` cuma snapshot teks). Objek biasa, bukan
 * `Map`, karena ia menyeberang dari server ke klien lewat payload RSC.
 */
export type CategoryCovers = Record<string, string[]>;

const hasOwn = (record: object, key: string) =>
  Object.prototype.hasOwnProperty.call(record, key);

/**
 * Id produk yang foto sampulnya perlu ditarik untuk tile kategori:
 * `perCategory` produk discoverable PERTAMA per kategori, menurut urutan
 * `products` — index katalog sudah terbaru-dulu (`created_at desc`), jadi
 * tile menampilkan barang paling baru. Hanya kategori di `categoryNames`
 * (master ber-produk) yang dihitung; produk ber-kategori yatim diabaikan.
 */
export function categoryCoverCandidateIds<T extends HomeCatalogItem>(
  products: T[],
  categoryNames: string[],
  perCategory: number = CATEGORY_COVER_CANDIDATES
): string[] {
  const quota = new Map<string, number>();
  for (const name of categoryNames) {
    const key = normalize(name);
    if (key) quota.set(key, perCategory);
  }
  const ids: string[] = [];
  for (const product of products) {
    if (!isDiscoverableProduct(product)) continue;
    const key = normalize(product.category);
    const left = quota.get(key) ?? 0;
    if (left <= 0) continue;
    quota.set(key, left - 1);
    ids.push(product.id);
  }
  return ids;
}

/**
 * Foto tile kategori: maksimal `max` foto sampul per kategori, diambil dari
 * produk discoverable TERBARU yang punya foto (`coverById`, hasil tarik
 * `categoryCoverCandidateIds`). Produk tanpa foto dilompati; foto yang sama
 * persis tak dipakai dua kali. Kategori yang tak punya satu foto pun TIDAK
 * masuk hasil — pemanggil jatuh ke ikon bawaan.
 */
export function categoryCoverPhotos<T extends HomeCatalogItem>(
  products: T[],
  categoryNames: string[],
  coverById: Readonly<Record<string, string>>,
  max: number = CATEGORY_COVER_PHOTOS
): CategoryCovers {
  const wanted = new Set(categoryNames.map(normalize).filter(Boolean));
  const covers = new Map<string, string[]>();
  for (const product of products) {
    if (!isDiscoverableProduct(product)) continue;
    const key = normalize(product.category);
    if (!wanted.has(key)) continue;
    const url = hasOwn(coverById, product.id) ? coverById[product.id] : "";
    if (!url) continue;
    const list = covers.get(key) ?? [];
    if (list.length >= max || list.includes(url)) continue;
    list.push(url);
    covers.set(key, list);
  }
  // `fromEntries` mendefinisikan properti sendiri, jadi kategori bernama
  // "__proto__" pun aman — penugasan `obj[key] = …` akan mengganti prototipe.
  return Object.fromEntries(covers);
}

/** Foto tile satu kategori dari `categoryCoverPhotos` — nama apa adanya. */
export function categoryCoversOf(
  covers: CategoryCovers | undefined,
  name: string
): string[] {
  if (!covers) return [];
  const key = normalize(name);
  return hasOwn(covers, key) ? covers[key] : [];
}
